import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MasterFormPage from "../common/MasterFormPage";
import MasterShell from "../common/MasterShell";
import MasterToast from "../common/MasterToast";
import { useCapabilities } from "../../contexts/CapabilitiesContext";
import { getDistricts } from "../../services/districtService";
import {
  createStation,
  getStationById,
  STATION_UPDATE_SUPPORTED,
  updateStation,
} from "../../services/stationService";
import { getSubDivisions } from "../../services/subDivisionService";
import {
  extractApiErrorMessage,
  getDefaultUserState,
  toSelectOptions,
} from "../../utils/masterHelpers";
import { getMasterHierarchyAccess } from "../../utils/hierarchyScope";
import "./Stations.css";

const createInitialValues = () => ({
  code: "",
  district_id: "",
  name: "",
  state: getDefaultUserState(),
  sub_division_id: "",
});

export default function StationForm({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { loading: capabilitiesLoading, user } = useCapabilities();
  const [allSubDivisions, setAllSubDivisions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({
    message: "",
    open: false,
    severity: "success",
  });
  const [values, setValues] = useState(createInitialValues);
  const isEditMode = mode === "edit";
  const readOnly = isEditMode && !STATION_UPDATE_SUPPORTED;

  const scopeAccess = useMemo(
    () => getMasterHierarchyAccess({ user }),
    [user]
  );
  const {
    canSelectDistrict,
    canSelectSubDivision,
    fixedDistrictId,
    fixedSubDivisionId,
  } = scopeAccess;
  const effectiveDistrictId = canSelectDistrict
    ? values.district_id
    : fixedDistrictId;
  const effectiveSubDivisionId = canSelectSubDivision
    ? values.sub_division_id
    : fixedSubDivisionId;

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [districts, subDivisions] = await Promise.all([
        canSelectDistrict ? getDistricts() : Promise.resolve([]),
        canSelectSubDivision
          ? getSubDivisions(
              fixedDistrictId ? { district_id: fixedDistrictId } : {}
            )
          : Promise.resolve([]),
      ]);

      setDistrictOptions(toSelectOptions(districts));
      setAllSubDivisions(Array.isArray(subDivisions) ? subDivisions : []);

      if (isEditMode) {
        if (!id) {
          setToast({
            message: "Station ID is missing.",
            open: true,
            severity: "error",
          });
          return;
        }

        const selectedStation = await getStationById(id);

        if (!selectedStation) {
          setToast({
            message: "Station not found.",
            open: true,
            severity: "error",
          });
        } else {
          setValues({
            code: selectedStation.code || "",
            district_id: selectedStation.district_id || "",
            name: selectedStation.name || "",
            state: selectedStation.state || "",
            sub_division_id: selectedStation.sub_division_id || "",
          });
        }
      }
    } catch (apiError) {
      setToast({
        message: extractApiErrorMessage(
          apiError,
          "Failed to load station details."
        ),
        open: true,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [canSelectDistrict, canSelectSubDivision, fixedDistrictId, id, isEditMode]);

  useEffect(() => {
    if (capabilitiesLoading) {
      return;
    }

    loadData();
  }, [capabilitiesLoading, loadData]);

  useEffect(() => {
    if (isEditMode || capabilitiesLoading) {
      return;
    }

    setValues((currentValues) => ({
      ...currentValues,
      district_id: canSelectDistrict ? currentValues.district_id : fixedDistrictId,
      sub_division_id: canSelectSubDivision
        ? currentValues.sub_division_id
        : fixedSubDivisionId,
    }));
  }, [
    capabilitiesLoading,
    canSelectDistrict,
    canSelectSubDivision,
    fixedDistrictId,
    fixedSubDivisionId,
    isEditMode,
  ]);

  const filteredSubDivisions = useMemo(() => {
    const items = effectiveDistrictId
      ? allSubDivisions.filter(
          (item) => item.district_id === effectiveDistrictId
        )
      : allSubDivisions;

    return toSelectOptions(items);
  }, [allSubDivisions, effectiveDistrictId]);

  const fields = useMemo(
    () => [
      {
        name: "district_id",
        label: "District",
        placeholder: "Select district",
        required: true,
        type: "select",
        options: districtOptions,
        error: errors.district_id,
        hidden: !canSelectDistrict,
      },
      {
        name: "sub_division_id",
        label: "Sub-division",
        placeholder: "Select sub-division",
        required: true,
        type: "select",
        options: filteredSubDivisions,
        error: errors.sub_division_id,
        disabled: isEditMode || !effectiveDistrictId,
        hidden: !canSelectSubDivision,
      },
      {
        name: "code",
        label: "Code",
        placeholder: "Enter station code",
        required: true,
        error: errors.code,
      },
      {
        name: "name",
        label: "Name",
        placeholder: "Enter station name",
        required: true,
        error: errors.name,
      },
      {
        name: "state",
        label: "State",
        placeholder: "Enter state name",
        required: true,
        error: errors.state,
      },
    ],
    [
      canSelectDistrict,
      canSelectSubDivision,
      districtOptions,
      errors.code,
      errors.district_id,
      errors.name,
      errors.state,
      errors.sub_division_id,
      filteredSubDivisions,
      effectiveDistrictId,
      isEditMode,
    ]
  );

  const validate = () => {
    const nextErrors = {};

    if (!effectiveDistrictId) {
      nextErrors.district_id = "District is required.";
    }

    if (!effectiveSubDivisionId) {
      nextErrors.sub_division_id = "Sub-division is required.";
    }

    if (!values.code.trim()) {
      nextErrors.code = "Station code is required.";
    }

    if (!values.name.trim()) {
      nextErrors.name = "Station name is required.";
    }

    if (!values.state.trim()) {
      nextErrors.state = "State is required.";
    }

    setErrors({
      ...nextErrors,
      ...(canSelectDistrict ? {} : { district_id: "" }),
      ...(canSelectSubDivision ? {} : { sub_division_id: "" }),
    });
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setValues((currentValues) => {
      const nextValues = {
        ...currentValues,
        [name]: value,
      };

      if (name === "district_id") {
        nextValues.sub_division_id = "";
      }

      return nextValues;
    });

    setErrors((currentErrors) => ({
      ...currentErrors,
      [name]: "",
      ...(name === "district_id" ? { sub_division_id: "" } : {}),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);

    try {
      let response;

      if (isEditMode) {
        if (!id) {
          setToast({
            message: "Station ID is missing.",
            open: true,
            severity: "error",
          });
          return;
        }

        response = await updateStation(id, {
          code: values.code.trim(),
          district_id: effectiveDistrictId || null,
          name: values.name.trim(),
          state: values.state.trim(),
          sub_division_id: effectiveSubDivisionId || null,
        });
      } else {
        response = await createStation({
          code: values.code.trim(),
          district: "",
          district_id: effectiveDistrictId || null,
          name: values.name.trim(),
          state: values.state.trim(),
          sub_division_id: effectiveSubDivisionId || null,
        });
      }

      navigate("/stations", {
        replace: true,
        state: {
          toast: {
            message:
              response?.message ||
              (isEditMode
                ? "Station updated successfully."
                : "Station created successfully."),
            severity: "success",
          },
        },
      });
    } catch (apiError) {
      setToast({
        message: extractApiErrorMessage(
          apiError,
          isEditMode ? "Failed to update station." : "Failed to create station."
        ),
        open: true,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MasterShell pageClassName="stations-module">
      {loading || capabilitiesLoading ? (
        <div className="masters-state">
          <div className="masters-state__loader" />
          <h2 className="masters-state__title">Loading station</h2>
          <p className="masters-state__text">
            Please wait while the station data is being prepared.
          </p>
        </div>
      ) : (
        <MasterFormPage
          description={
            isEditMode
              ? "Review and update station details and linked geography."
              : "Create a station and connect it to the correct district and sub-division."
          }
          fields={fields}
          onBack={() => navigate("/stations")}
          onChange={handleChange}
          onSubmit={handleSubmit}
          readOnly={readOnly}
          submitDisabled={false}
          submitLabel={isEditMode ? "Save Changes" : "Save Station"}
          submitNote=""
          submitting={submitting}
          title={isEditMode ? "Edit Station" : "Add Station"}
          values={values}
        />
      )}

      <MasterToast
        message={toast.message}
        onClose={() =>
          setToast((currentToast) => ({ ...currentToast, open: false }))
        }
        open={toast.open}
        severity={toast.severity}
      />
    </MasterShell>
  );
}
